import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { VehicleCard } from "../components/VehicleCard";
import { VehicleDetailModal } from "../components/fleet/VehicleDetailModal";
import { AgeGateModal } from "../components/booking/AgeGateModal";
import api, { apiError, setDraft } from "../lib/api";
import { Loader2, SlidersHorizontal, Pencil, Check, Sparkles } from "lucide-react";
import dayjs from "dayjs";

const familyOf = (c) => {
  const s = c.toLowerCase();
  if (s.includes("luxury") || s.includes("hot hatch") || s.includes("special premium")) return "Luxury & Performance";
  if (s.includes("7-seat") || s.includes("mpv")) return "7-Seaters & MPV";
  if (s.includes("suv") || s.includes("crossover")) return "SUVs & Crossovers";
  if (s.includes("hatchback") || s.includes("subcompact")) return "Economy";
  return "Sedans";
};
const FAMILY_ORDER = ["All", "Economy", "Sedans", "SUVs & Crossovers", "7-Seaters & MPV", "Luxury & Performance"];

const INCLUDED_IN_EVERY = ["Collision Damage Waiver and third-party cover over 1 million USD", "Unlimited mileage", "24/7 roadside assistance", "Accident or breakdown replacement"];

const cap = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export const handoverFromParams = (params) => ({
  type: params.get("handover") === "delivery" ? "delivery" : "branch",
  branchId: params.get("branch") || "DXB-T3",
  emirate: params.get("emirate") || "dubai",
  useCurrentLocation: params.get("cur") === "1",
  returnType: params.get("return_type") || (params.get("handover") === "delivery" ? "collect" : "branch"),
  returnBranchId: params.get("return_branch") || params.get("branch") || "DXB-T3",
  returnEmirate: params.get("return_emirate") || params.get("emirate") || "dubai",
  returnUseCurrentLocation: params.get("return_cur") === "1",
});

export default function Results() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [category, setCategory] = useState("All");
  const [sort, setSort] = useState("price_asc");
  const [ageOk, setAgeOk] = useState(params.get("age_ok") === "1");
  const [ageChecked, setAgeChecked] = useState(false);
  const [pendingVehicle, setPendingVehicle] = useState(null);
  const [detail, setDetail] = useState(null);
  const [galleries, setGalleries] = useState({});

  useEffect(() => {
    api.get("/fleet/galleries").then((r) => setGalleries(r.data)).catch(() => {});
  }, []);

  const handover = useMemo(() => handoverFromParams(params), [params]);

  useEffect(() => {
    const body = {
      mode: "short_term",
      pickup_datetime: params.get("pickup"),
      return_datetime: params.get("return"),
      driver_age: 25,
    };
    if (handover.type === "delivery") body.delivery_emirate = handover.emirate;
    else {
      body.pickup_station = handover.branchId;
      body.dropoff_station = handover.returnType === "branch" ? handover.returnBranchId : handover.branchId;
    }
    let ignore = false;
    api.post("/vehicles/search", body)
      .then((r) => { if (!ignore) setData(r.data); })
      .catch((e) => { if (!ignore) setError(apiError(e)); });
    return () => { ignore = true; };
  }, [params, handover]);

  const editSearchUrl = `/?${params.toString()}#book`;

  if (error)
    return (
      <div className="min-h-screen flex flex-col bg-[#fafcfa]">
        <Header />
        <div className="flex-1 flex flex-col items-center justify-center gap-4 px-4 text-center" data-testid="results-error">
          <p className="text-sm text-[#57685e] max-w-md">{error}</p>
          <Link to={editSearchUrl} className="btn-cta rounded px-6 py-3 text-xs" data-testid="adjust-search-btn">{t("Adjust my search, everything is saved")}</Link>
        </div>
        <Footer />
      </div>
    );

  if (!data)
    return (
      <div className="min-h-screen flex flex-col bg-[#fafcfa]">
        <Header />
        <div className="flex-1 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#009900]" /></div>
        <Footer />
      </div>
    );

  const categories = FAMILY_ORDER.filter((f) => f === "All" || data.vehicles.some((v) => familyOf(v.category) === f));
  let vehicles = data.vehicles.filter((v) => category === "All" || familyOf(v.category) === category);
  vehicles = [...vehicles].sort((a, b) => sort === "price_desc"
    ? b.pricing.total_with_vat - a.pricing.total_with_vat
    : a.pricing.total_with_vat - b.pricing.total_with_vat);

  const cheapestId = vehicles.length
    ? data.vehicles.reduce((a, b) => (a.pricing.total_with_vat <= b.pricing.total_with_vat ? a : b)).id
    : null;
  const popularPool = data.vehicles.filter((v) => v.category === "Compact sedan" && v.id !== cheapestId);
  const popularId = popularPool.length ? popularPool.reduce((a, b) => (a.pricing.total_with_vat <= b.pricing.total_with_vat ? a : b)).id : null;
  const badgeFor = (v) => (v.id === cheapestId ? "Best value" : v.id === popularId ? "Most popular" : null);
  const daysToWeekly = data.search.days >= 4 && data.search.days < 7 ? 7 - data.search.days : 0;

  const lockIn = (vehicle) => {
    setDraft({
      vehicle,
      search: data.search,
      handover,
      flight: params.get("flight_no") ? { number: params.get("flight_no"), arrival: params.get("flight_arr") } : null,
    });
    navigate("/booking");
  };

  const onSelect = (vehicle) => {
    if (!ageOk) {
      setPendingVehicle(vehicle);
      return;
    }
    lockIn(vehicle);
  };

  return (
    <div className="min-h-screen flex flex-col bg-[#fafcfa]">
      <Header />
      <div className="bg-[#144733] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center gap-3 text-sm" data-testid="search-summary-bar">
          <span className="bg-[#009900] rounded px-2 py-0.5 font-semibold uppercase tracking-wide text-[10px]">{t("{{n}} days", { n: data.search.days })}</span>
          <span>{dayjs(data.search.pickup_datetime).format("DD MMM HH:mm")} → {dayjs(data.search.return_datetime).format("DD MMM HH:mm")}</span>
          <span className="text-white/70">
            {handover.type === "delivery" ? t("Delivered in {{place}}", { place: cap(handover.emirate) }) : data.search.pickup_station?.name}
          </span>
          <Link to={editSearchUrl} className="ml-auto flex items-center gap-1.5 text-[#FFDC00] text-xs font-semibold uppercase tracking-wide hover:underline" data-testid="edit-search-btn">
            <Pencil className="w-3.5 h-3.5" /> {t("Edit search")}
          </Link>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 w-full flex-1">
        {!ageOk && (
          <div className="bg-[#FFDC00]/20 border border-[#FFDC00] rounded-lg px-4 py-3 text-xs text-[#0F1E17] mb-6" data-testid="age-pending-notice">
            {t("You're viewing live rates. We'll ask you to confirm you're above 21 when you select a vehicle.")}
          </div>
        )}

        <div className="bg-white border border-[#e2ebe4] rounded-lg px-4 py-3 mb-4 flex flex-wrap items-center gap-x-5 gap-y-1.5" data-testid="results-included-strip">
          <span className="text-[10px] uppercase tracking-widest font-semibold text-[#144733]">{t("Every rental includes")}</span>
          {INCLUDED_IN_EVERY.map((i) => (
            <span key={i} className="flex items-center gap-1.5 text-xs text-[#57685e]"><Check className="w-3.5 h-3.5 text-[#009900]" />{t(i)}</span>
          ))}
        </div>

        {daysToWeekly > 0 && (
          <div className="bg-[#f0f9f0] border border-[#009900]/25 rounded-lg px-4 py-3 mb-6 flex flex-wrap items-center gap-3 text-xs text-[#144733]" data-testid="weekly-nudge-banner">
            <Sparkles className="w-4 h-4 text-[#009900] shrink-0" />
            <span><strong>{t("Smart-money tip:")}</strong> {t("add {{n}} more days and you unlock the weekly band rate, often a lower total for more time with the car.", { n: daysToWeekly })}</span>
            <Link to={editSearchUrl} className="font-semibold text-[#009900] underline underline-offset-2 hover:text-[#144733]" data-testid="weekly-nudge-edit">{t("Adjust dates")}</Link>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 mb-6">
          <SlidersHorizontal className="w-4 h-4 text-[#57685e]" />
          <div className="flex flex-wrap gap-2" data-testid="category-filters">
            {categories.map((c) => (
              <button key={c} onClick={() => setCategory(c)}
                className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${category === c ? "bg-[#009900] text-white border-[#009900]" : "bg-white text-[#144733] border-[#d5ddd5] hover:border-[#009900]"}`}>
                {t(c)}
              </button>
            ))}
          </div>
          <span className="text-xs text-[#57685e] ml-auto" data-testid="results-count">{t("{{n}} cars available", { n: vehicles.length })}</span>
          <select className="field-input !w-auto text-xs" value={sort} onChange={(e) => setSort(e.target.value)} data-testid="sort-select">
            <option value="price_asc">{t("Price: low to high")}</option>
            <option value="price_desc">{t("Price: high to low")}</option>
          </select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="results-grid">
          {vehicles.map((v) => <VehicleCard key={v.id} vehicle={v} mode="short_term" onSelect={onSelect} onDetails={setDetail} badge={badgeFor(v)} />)}
        </div>
        {vehicles.length === 0 && (
          <div className="bg-white border border-[#e2ebe4] rounded-lg px-6 py-12 text-center text-sm text-[#57685e]" data-testid="results-empty-state">
            {t("No cars match this search")}{category !== "All" ? t(" in this category") : ""}.{" "}
            <Link to={editSearchUrl} className="text-[#009900] font-semibold underline underline-offset-2">{t("adjust your dates")}</Link>.
          </div>
        )}
      </main>

      <VehicleDetailModal vehicle={detail} gallery={detail ? galleries[detail.id] : null}
        onClose={() => setDetail(null)} onSelect={(v) => { setDetail(null); onSelect(v); }} />
      <AgeGateModal open={!!pendingVehicle} onClose={() => setPendingVehicle(null)}
        checked={ageChecked} setChecked={setAgeChecked}
        onConfirm={() => { setAgeOk(true); lockIn(pendingVehicle); }} />
      <Footer />
    </div>
  );
}
